import { Line } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { useEffect, useMemo } from "react";
import { formatDateToNumberMMYY } from "@utils/formatDate";
import { useAuthContext } from "@contexts/auth";

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)


const ProjectLineChart = () => {
  const { activeProject: { followUps = [], cashFlowPlanning }, selectedGraph } = useProjectContext();
  const { selectXlsxData } = useProjectActions()
  const { user: { configurations } } = useAuthContext();
  const { financial: financialConfig = [] } = configurations.dashboard.graphs
  // Sem previsto/medido/físico-financeiro (`physicalFinancial.available`, decidido pelo
  // BE a partir da série completa da obra), o gráfico não tem o que desenhar -- um
  // gráfico "Avanço Físico Financeiro" sem nenhuma linha pareceria quebrado, não
  // ausente (serve-works-without-optional-sources, D5).
  const physicalFinancialAvailable = cashFlowPlanning?.physicalFinancial?.available === true;


  const { base, measured, baseline, scheduled, dates } = useMemo(() => followUps.reduce((accumulator, followUp) => {
    accumulator.base.push(followUp.base != null ? followUp.base * 100 : undefined)
    accumulator.measured.push(followUp.measured != null ? followUp.measured * 100 : undefined)
    accumulator.baseline.push(followUp.baseline != null ? followUp.baseline * 100 : undefined)
    accumulator.scheduled.push(followUp.scheduled != null ? followUp.scheduled * 100 : undefined)
    accumulator.dates.push(formatDateToNumberMMYY(followUp.date))
    return accumulator
  }, {
    base: [],
    measured: [],
    baseline: [],
    scheduled: [],
    dates: [],
  }), [followUps])

  useEffect(() => {
    const fields = [
      { label: 'Data', key: 'date' },
      { label: 'Medido', key: 'measured' },
      { label: 'Linha de base', key: 'base' },
      { label: 'Previsto', key: 'scheduled' },
    ]
    const resources = dates.map((date, index) => ({
      date,
      base: base[index],
      measured: measured[index],
      scheduled: scheduled[index],
      baseline: baseline[index]
    }));

    selectXlsxData({ fields, resources })

  }, [selectedGraph, dates, base, measured, scheduled, baseline])

  const checkGraphConfig = (e) => {
    switch (e) {
      case 'measured':
        return {
          label: "Medido",
          borderColor: "#3acff7",
          backgroundColor: "#3acff7",
          borderWidth: 3,
          data: measured,
        }
      case 'scheduled':
        return {
          label: "Previsto",
          borderColor: "#FFA07A",
          backgroundColor: "#FFA07A",
          borderWidth: 3,
          data: scheduled,
        }
      case 'base':
        return {
          label: "Linha de base",
          borderColor: "#FFD700",
          backgroundColor: "#FFD700",
          borderWidth: 3,
          borderDash: [6, 4],
          data: base,
        }
      default:
        break;
    }
  }

  if (!physicalFinancialAvailable) return null;

  return (
    <Line options={{
      responsive: true,
      maintainAspectRatio: false,
      // aspectRatio: 1|3,
      height: 500,
      plugins: {
        legend: {
          display: true,
          position: "top",
          align: "center",
        },
        datalabels: {
          display: false
        },
        title: {
          display: false,
          text: "Avanço Físico Financeiro",
          align: "start",
          color: "black",
          font: {
            size: "18px"
          }
        }
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Mês/Ano',
            font: {
              weight: 'bold'
            }
          }
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Porcentagem',
            font: {
              weight: 'bold'
            }
          }
        }
      }
    }}
      data={{
        labels: dates,
        datasets: financialConfig.map(i => {
          return checkGraphConfig(i)
        }).filter(Boolean),
      }} />
  )
}

export default ProjectLineChart;
